import { FC, useMemo } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { InnerBox } from "@/components/elements/InnerBox";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { Button } from "@/components/elements/Button";
import { CompanyFormTitle } from "@/features/company/components/company-info/CompanyFormTitle";
import { TCompanyUserForm } from "@/features/company/types/CompanyUserForm";
import { useFormContext } from "react-hook-form";
import { USER_ACTIVE_STATUS, USER_ACTIVE_STATUS_LABEL } from "@/types/enum";
import { TOption } from "@/types/common";
import { background, button, divider, text } from "@/theme/colors";
import { TCompanyUser } from "@/api/entities/company-user.entity";

type Props = {
  userData?: TCompanyUser;
  onPasswordReset?: () => void;
  isPasswordResetLoading?: boolean;
  isCreateMode?: boolean;
};

export const CompanyUserFormInfo: FC<Props> = ({ userData, onPasswordReset, isPasswordResetLoading, isCreateMode }) => {
  const { control } = useFormContext<TCompanyUserForm>();

  const activeStatusOptions: TOption[] = useMemo(
    () => [
      { label: USER_ACTIVE_STATUS_LABEL[USER_ACTIVE_STATUS.ACTIVE], value: USER_ACTIVE_STATUS.ACTIVE },
      { label: USER_ACTIVE_STATUS_LABEL[USER_ACTIVE_STATUS.INACTIVE], value: USER_ACTIVE_STATUS.INACTIVE },
    ],
    [],
  );

  const cognitoStatusLabel = useMemo(() => {
    if (isCreateMode) return "登録メール送信前";
    if (userData?.cognitoStatus === undefined) return "-";
    if (userData?.cognitoStatus === 0) return "登録メール送付済み";
    return "パスワード設定済み"; // Default fallback if needed
  }, [userData?.cognitoStatus, isCreateMode]);

  return (
    <InnerBox title={<CompanyFormTitle text="ユーザー情報" />} showDivider={false}>
      <Grid container columns={10} columnSpacing={3} rowSpacing={2.5}>
        <Grid item xs={4}>
          {isCreateMode ? (
            <Box width="100%" pt="1px" height="100%" display="flex" flexDirection="column" justifyContent="center">
              <Box display="flex" alignItems="center" gap="2px" mb="4px">
                <Typography variant="cap12" color={text.primary}>
                  ステータス
                </Typography>
              </Box>
              <Typography variant="body14" fontWeight={500} minHeight="40px" display="flex" alignItems="center" whiteSpace="nowrap">
                {USER_ACTIVE_STATUS_LABEL[USER_ACTIVE_STATUS.ACTIVE]}
              </Typography>
            </Box>
          ) : (
            <FormField control={control} name="activeStatus">
              <Select options={activeStatusOptions} placeholder="選択してください" />
            </FormField>
          )}
        </Grid>
        <Grid item xs={4}>
          <Box width="100%" pt="1px" height="100%" display="flex" flexDirection="column" justifyContent="center">
            <Box display="flex" alignItems="center" gap="2px" mb="4px">
              <Typography variant="cap12" color={text.primary}>
                認証ステータス
              </Typography>
            </Box>
            <Typography variant="body14" fontWeight={400} minHeight="40px" display="flex" alignItems="center" whiteSpace="nowrap">
              {cognitoStatusLabel}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={2} display="flex" alignItems="center">
          <Button
            variant="secondary"
            size="sm"
            onClick={onPasswordReset}
            disabled={!onPasswordReset || isPasswordResetLoading || userData?.cognitoStatus === 0 || userData?.activeStatus === USER_ACTIVE_STATUS.INACTIVE}
            sx={{
              width: "100%",
              height: "40px",
              flexShrink: 0,
              "&.MuiButtonBase-root": {
                border: `2px solid ${button.disable}`,
                color: text.secondary,
                "&:hover, &:focus": {
                  background: divider.middle,
                  color: text.secondary,
                },
                "&:active": {
                  background: text.tertiary,
                  color: "#FFFFFF",
                },
                "&.Mui-disabled, &:disabled": {
                  backgroundColor: `${background.disable} !important`,
                  border: userData?.cognitoStatus === 0 ? `2px solid ${button.disable}` : "none !important",
                  color: text.tertiary,
                },
              }
            }}
          >
            パスワードリセット
          </Button>
        </Grid>
        <Grid item xs={5}>
          <FormField control={control} name="fullName">
            <Input />
          </FormField>
        </Grid>
        <Grid item xs={5}>
          <FormField control={control} name="email">
            <Input />
          </FormField>
        </Grid>
      </Grid>
    </InnerBox>
  );
};
